import React, { useEffect, useState } from "react";
import {
  Modal,
  Stack,
  Text,
  Card,
  Group,
  Button,
  TextInput,
  FileInput,
  ThemeIcon,
  Radio,
  Switch,
  Select,
  NumberInput,
  MultiSelect,
  ActionIcon,
  Badge,
} from "@mantine/core";
import { IconFileText, IconPlus, IconTrash } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { esignatureService } from "../../services/esignature";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import { RecipientsStep, RoleFormModal } from "./UploadAgreementModal";
import EditableScaleCard from "./EditableScaleCard";
import MdSplitSection from "./MdSplitSection";
import UpfrontPayDisplay from "./UpfrontPayDisplay";
import BonusDisplay from "./BonusDisplay";
import AddBonusForm from "./AddBonusForm";
import ExpenseDisplay from "./ExpenseDisplay";
import AddExpenseForm from "./AddExpenseForm";
import DealReview from "./DealReview";

const NEW_AGREEMENT_ID = "__new__";

const UPFRONT_PAY_TYPE_OPTIONS = [
  { value: "FIXED_AMOUNT_PER_ACCOUNT", label: "Fixed Amount Per Account" },
  { value: "SCALE_AMOUNT_PER_ACCOUNT", label: "Scaled Amount Per Account" },
  { value: "SCALE_AMOUNT_TOTAL", label: "Scaled Amount Total" },
  { value: "FIXED_PERCENT_ACV", label: "Fixed Percentage of ACV" },
  { value: "SCALE_PERCENT_ACV", label: "Scaled Percentage of ACV" },
  { value: "INITIAL_PRICE", label: "Initial Price" },
];

const UPFRONT_PAY_TYPE_LABEL_BY_VALUE = UPFRONT_PAY_TYPE_OPTIONS.reduce(
  (acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
  },
  {}
);

const AMOUNT_SCALE_TYPES = ["SCALE_AMOUNT_PER_ACCOUNT", "SCALE_AMOUNT_TOTAL"];
const SCALE_TYPES = [...AMOUNT_SCALE_TYPES, "SCALE_PERCENT_ACV"];
const emptyScaleLine = () => ({ accounts: "", value: "" });

// Creating a custom deal:
//   Step 1 - pick (or upload) the agreement the deal is based on.
//   Step 2 - choose the deal type (and, for REP, whether overrides are allowed).
const emptyLine = () => ({ revenue_amount: "", percentage: "" });

// Map an embedded scale object's lines into the editor's shape (used to prefill
// when editing an existing deal template).
const linesFromScaleObj = scale =>
  scale && Array.isArray(scale.scale_lines) && scale.scale_lines.length > 0
    ? scale.scale_lines.map(line => ({
        revenue_amount: line.revenue_amount ?? "",
        percentage: line.percentage ?? "",
      }))
    : [emptyLine()];

// Map a raw list of scale lines into the editor's shape.
const linesFromList = lines =>
  Array.isArray(lines) && lines.length > 0
    ? lines.map(line => ({
        revenue_amount: line.revenue_amount ?? "",
        percentage: line.percentage ?? "",
      }))
    : [emptyLine()];

// A deal user's commission lines come back either as an embedded commission_scale
// object (company template) or a flat commission_scale_lines array (manager deal).
const commissionLinesFromUser = u =>
  u?.commission_scale
    ? linesFromScaleObj(u.commission_scale)
    : linesFromList(u?.commission_scale_lines);

// A user's bonuses arrive under default_bonuses (company template) or bonuses
// (manager deal); both are arrays of full bonus objects.
const bonusesFromUser = u =>
  Array.isArray(u?.default_bonuses)
    ? u.default_bonuses
    : Array.isArray(u?.bonuses)
      ? u.bonuses
      : [];

// Convert editor scale lines ({ revenue_amount, percentage }) into the numeric
// payload shape, dropping any blank rows.
const toScaleLinesPayload = lines =>
  (Array.isArray(lines) ? lines : [])
    .filter(
      l =>
        l.revenue_amount !== "" &&
        l.revenue_amount != null &&
        l.percentage !== "" &&
        l.percentage != null
    )
    .map(l => ({
      revenue_amount: Number(l.revenue_amount),
      percentage: Number(l.percentage),
    }));

// `deal` (optional) puts the modal in edit mode: every step is prefilled from
// the existing deal template and saving updates it (PUT) instead of creating.
function CreateCustomDealModal({
  opened,
  onClose,
  agreements,
  onSaved,
  sellingYearId,
  scales,
  upfrontPay = [],
  defaultBonuses = [],
  // Active company pay types from the overview, used to source the upfront pay
  // type, bonus category, and expense type options.
  companyUpfrontTypes = [],
  companyBonusTypes = [],
  companyExpenseTypes = [],
  deal = null,
  // When true the modal prefills from `deal` (a company template) but saves as a
  // NEW manager deal (POST /v1/ps/manager-deal) instead of editing.
  managerCreate = false,
  // Company deal templates a manager can start from. In managerCreate mode the
  // wizard's first step picks one of these as the "initial deal" to customize.
  companyTemplates = [],
  // When true the modal prefills from `deal` (the manager's own template) and
  // saves as an edit to that manager deal (PUT /v1/ps/manager-deal/{id}).
  managerEdit = false,
  // Embedded editing mode: skip the agreement/template step (start at deal type)
  // and, instead of saving, hand the edited deal payload back via onComplete.
  hideTemplateStep = false,
  onComplete = null,
  // Render the wizard inline (no Modal wrapper) so a host modal can show the
  // steps in place instead of stacking a second modal on top.
  embedded = false,
  // In embedded mode, called when the user backs out of the first wizard step
  // (returns control to the host's previous screen).
  onBack = null,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showError, showSuccess } = useToast();

  // A manager deal (create-from-company-template or edit-own) uses the
  // /v1/ps/manager-deal endpoint and the full-objects payload shape.
  const isManagerDeal = managerCreate || managerEdit;
  const editingId = managerCreate ? null : (deal?.id ?? null);
  const isEditing = editingId != null;

  const [step, setStep] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  // managerCreate mode only: the company template chosen on the first step.
  const [managerTemplateId, setManagerTemplateId] = useState(null);

  // Name and customizable flag for the deal template (set on the review step).
  const [dealName, setDealName] = useState("");
  const [isCustomizable, setIsCustomizable] = useState(true);

  // New-agreement fields.
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [savedAgreement, setSavedAgreement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleModalOpened, setRoleModalOpened] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  // Step 2 - deal type.
  const [dealType, setDealType] = useState("REP");
  const [allowOverride, setAllowOverride] = useState(false);
  const [populateScaleId, setPopulateScaleId] = useState(null);
  const [overrideLines, setOverrideLines] = useState([emptyLine()]);
  // Document text id of the override scale the deal is sourced from.
  const [overrideDocumentTextId, setOverrideDocumentTextId] = useState(null);
  const [mdPopulateScaleId, setMdPopulateScaleId] = useState(null);
  const [mdLines, setMdLines] = useState([emptyLine()]);
  // Document text id of the MD scale the deal is sourced from.
  const [mdDocumentTextId, setMdDocumentTextId] = useState(null);
  const [splitMd, setSplitMd] = useState(false);
  const [splitUsers, setSplitUsers] = useState([]);

  // Step 3 - commission scale (one editor per user when splitting an MD deal).
  const [commissionPopulateScaleId, setCommissionPopulateScaleId] =
    useState(null);
  const [commissionLines, setCommissionLines] = useState([emptyLine()]);
  // Per-user commission editors keyed by split-user index:
  //   { [index]: { populateScaleId, lines } }
  const [userCommission, setUserCommission] = useState({});

  // Step 4 - upfront pay. The non-split flow edits the upfront details inline as
  // a form and carries them straight into the deal payload (no separate save).
  const [upPayType, setUpPayType] = useState(null);
  const [upFixedAmount, setUpFixedAmount] = useState("");
  const [upFixedPercentage, setUpFixedPercentage] = useState("");
  const [upScaleLines, setUpScaleLines] = useState([emptyScaleLine()]);
  const [savingUpfront, setSavingUpfront] = useState(false);
  const [createdUpfront, setCreatedUpfront] = useState([]);
  // Per-user upfront selection (by split-user index) when splitting an MD deal.
  const [userUpfront, setUserUpfront] = useState({});
  const [showNewUpfront, setShowNewUpfront] = useState(false);
  const [savingDeal, setSavingDeal] = useState(false);

  // Step 5 - bonuses. The non-split flow collects bonuses inline (full objects,
  // no separate save); `bonusFormKey` remounts the add form to reset it after
  // each bonus is added.
  const [addedBonuses, setAddedBonuses] = useState([]);
  const [bonusFormKey, setBonusFormKey] = useState(0);
  const [createdBonuses, setCreatedBonuses] = useState([]);
  const [showAddBonus, setShowAddBonus] = useState(false);
  // Per-user bonus selection (by split-user index) when splitting an MD deal.
  const [userBonuses, setUserBonuses] = useState({});

  const allBonuses = [
    ...(Array.isArray(defaultBonuses) ? defaultBonuses : []),
    ...createdBonuses,
  ];

  // Step 6 - expenses. Collected inline (full objects, no separate save);
  // `expenseFormKey` remounts the add form to reset it after each add.
  const [addedExpenses, setAddedExpenses] = useState([]);
  const [expenseFormKey, setExpenseFormKey] = useState(0);

  const allUpfront = [
    ...(Array.isArray(upfrontPay) ? upfrontPay : []),
    ...createdUpfront,
  ];

  const upShowScale = SCALE_TYPES.includes(upPayType);
  const upIsAmountScale = AMOUNT_SCALE_TYPES.includes(upPayType);

  // Existing options only (no "+ Add New Type") — used by the per-user selects
  // when splitting, where new types are created once via the shared form below.
  const existingUpfrontOptions = allUpfront.map(item => ({
    value: String(item.id),
    label: item.name,
  }));

  // Pay type options for the upfront form come from the company's active upfront
  // types (overview's ps_company_upfront_types), labeled from the static map.
  const upfrontPayTypeOptions = (
    Array.isArray(companyUpfrontTypes) ? companyUpfrontTypes : []
  )
    .filter(t => t?.is_active !== false && t?.active !== false)
    .map(t => {
      const value =
        t.type ??
        t.upfront_pay_type_category ??
        t.upfront_type_category ??
        t.upfront_pay_type ??
        null;
      return value
        ? { value, label: UPFRONT_PAY_TYPE_LABEL_BY_VALUE[value] ?? value }
        : null;
    })
    .filter(Boolean);

  // The upfront pay no longer has a dedicated name field; derive its name from
  // the selected pay type so it still reads sensibly in lists and payloads.
  const upfrontName = upPayType
    ? (UPFRONT_PAY_TYPE_LABEL_BY_VALUE[upPayType] ?? upPayType)
    : "";

  // Per-user upfront/bonus selections (by split-user index).
  const getUserUpfront = index => userUpfront[index] ?? null;
  const setUserUpfrontFor = (index, value) =>
    setUserUpfront(prev => ({ ...prev, [index]: value }));
  const getUserBonuses = index =>
    Array.isArray(userBonuses[index]) ? userBonuses[index] : [];
  const setUserBonusesFor = (index, value) =>
    setUserBonuses(prev => ({ ...prev, [index]: value }));

  const resetNewUpfrontForm = () => {
    setUpPayType(null);
    setUpFixedAmount("");
    setUpFixedPercentage("");
    setUpScaleLines([emptyScaleLine()]);
  };

  // Prefill the inline upfront form (non-split flow) from an existing deal's
  // upfront pay snapshot.
  const prefillUpfrontForm = upfront => {
    if (!upfront) {
      resetNewUpfrontForm();
      return;
    }
    setUpPayType(upfront.upfront_pay_type ?? null);
    setUpFixedAmount(
      upfront.fixed_amount != null ? String(upfront.fixed_amount) : ""
    );
    setUpFixedPercentage(
      upfront.fixed_percentage != null ? String(upfront.fixed_percentage) : ""
    );
    const lines = Array.isArray(upfront.scale_lines) ? upfront.scale_lines : [];
    setUpScaleLines(
      lines.length
        ? lines.map(line => ({
            accounts: line.accounts ?? "",
            value: line.amount ?? line.percentage ?? line.value ?? "",
          }))
        : [emptyScaleLine()]
    );
  };

  const bonusOptions = allBonuses.map(b => ({
    value: String(b.id),
    label: b.name,
  }));

  // Split flow: a newly created default bonus joins the shared pool so each user
  // can select it.
  const handleBonusCreated = created => {
    setCreatedBonuses(prev => [...prev, created]);
    setShowAddBonus(false);
  };

  // Non-split flow: append the inline bonus and reset the add form for another.
  const handleInlineBonusAdded = bonus => {
    setAddedBonuses(prev => [...prev, bonus]);
    setBonusFormKey(prev => prev + 1);
  };

  const removeAddedBonus = index =>
    setAddedBonuses(prev => prev.filter((_, i) => i !== index));

  // Expenses are collected inline (no separate save), like bonuses.
  const handleInlineExpenseAdded = expense => {
    setAddedExpenses(prev => [...prev, expense]);
    setExpenseFormKey(prev => prev + 1);
  };

  const removeAddedExpense = index =>
    setAddedExpenses(prev => prev.filter((_, i) => i !== index));

  const updateUpScaleLine = (index, field, value) =>
    setUpScaleLines(prev =>
      prev.map((l, i) => (i === index ? { ...l, [field]: value } : l))
    );
  const addUpScaleLine = () =>
    setUpScaleLines(prev => [...prev, emptyScaleLine()]);
  const removeUpScaleLine = index =>
    setUpScaleLines(prev =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );

  const handleAddSplitUser = () =>
    setSplitUsers(prev => [
      ...prev,
      { name: `User ${prev.length + 1}`, percentage: "" },
    ]);

  const updateSplitUser = (index, value) =>
    setSplitUsers(prev =>
      prev.map((u, i) => (i === index ? { ...u, percentage: value } : u))
    );

  const removeSplitUser = index =>
    setSplitUsers(prev => prev.filter((_, i) => i !== index));

  // Company default override scales feed the "populate from scale" dropdown.
  const overrideScales = Array.isArray(scales?.OVERRIDE) ? scales.OVERRIDE : [];
  const overrideScaleOptions = overrideScales.map(s => ({
    value: String(s.id),
    label: s.name,
  }));

  const handlePopulateFromScale = id => {
    setPopulateScaleId(id);
    const scale = overrideScales.find(s => String(s.id) === String(id));
    setOverrideDocumentTextId(
      scale ? (scale.document_text_id ?? scale.document_text?.id ?? null) : null
    );
    if (scale && Array.isArray(scale.scale_lines)) {
      setOverrideLines(
        scale.scale_lines.map(line => ({
          revenue_amount: line.revenue_amount ?? "",
          percentage: line.percentage ?? "",
        }))
      );
    }
  };

  const updateOverrideLine = (index, key, value) =>
    setOverrideLines(prev =>
      prev.map((l, i) => (i === index ? { ...l, [key]: value } : l))
    );

  const addOverrideLine = () =>
    setOverrideLines(prev => [...prev, emptyLine()]);

  const removeOverrideLine = index =>
    setOverrideLines(prev =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );

  // Company default commission scales feed the commission-scale step.
  const commissionScales = Array.isArray(scales?.COMMISSION)
    ? scales.COMMISSION
    : [];
  const commissionScaleOptions = commissionScales.map(s => ({
    value: String(s.id),
    label: s.name,
  }));

  const linesFromScale = (scaleId, fallback) => {
    const scale = commissionScales.find(s => String(s.id) === String(scaleId));
    return scale && Array.isArray(scale.scale_lines)
      ? scale.scale_lines.map(line => ({
          revenue_amount: line.revenue_amount ?? "",
          percentage: line.percentage ?? "",
        }))
      : fallback;
  };

  // Single (non-split) commission editor.
  const handlePopulateCommission = id => {
    setCommissionPopulateScaleId(id);
    setCommissionLines(prev => linesFromScale(id, prev));
  };
  const updateCommissionLine = (index, key, value) =>
    setCommissionLines(prev =>
      prev.map((l, i) => (i === index ? { ...l, [key]: value } : l))
    );
  const addCommissionLine = () =>
    setCommissionLines(prev => [...prev, emptyLine()]);
  const removeCommissionLine = index =>
    setCommissionLines(prev =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );

  // Per-user commission editors (used when splitting an MD deal).
  const defaultEditor = () => ({
    populateScaleId: null,
    lines: [emptyLine()],
  });
  const getUserCommission = index => userCommission[index] || defaultEditor();
  const patchUserCommission = (index, producer) =>
    setUserCommission(prev => {
      const cur = prev[index] || defaultEditor();
      return { ...prev, [index]: { ...cur, ...producer(cur) } };
    });
  const handlePopulateUserCommission = (index, id) =>
    patchUserCommission(index, cur => ({
      populateScaleId: id,
      lines: linesFromScale(id, cur.lines),
    }));
  const updateUserCommissionLine = (index, lineIndex, key, value) =>
    patchUserCommission(index, cur => ({
      lines: cur.lines.map((l, li) =>
        li === lineIndex ? { ...l, [key]: value } : l
      ),
    }));
  const addUserCommissionLine = index =>
    patchUserCommission(index, cur => ({ lines: [...cur.lines, emptyLine()] }));
  const removeUserCommissionLine = (index, lineIndex) =>
    patchUserCommission(index, cur => ({
      lines:
        cur.lines.length > 1
          ? cur.lines.filter((_, li) => li !== lineIndex)
          : cur.lines,
    }));

  // Company default MD scales feed the MD "populate from scale" dropdown.
  const mdScales = Array.isArray(scales?.MD) ? scales.MD : [];
  const mdScaleOptions = mdScales.map(s => ({
    value: String(s.id),
    label: s.name,
  }));

  const handlePopulateMdScale = id => {
    setMdPopulateScaleId(id);
    const scale = mdScales.find(s => String(s.id) === String(id));
    setMdDocumentTextId(
      scale ? (scale.document_text_id ?? scale.document_text?.id ?? null) : null
    );
    if (scale && Array.isArray(scale.scale_lines)) {
      setMdLines(
        scale.scale_lines.map(line => ({
          revenue_amount: line.revenue_amount ?? "",
          percentage: line.percentage ?? "",
        }))
      );
    }
  };

  const updateMdLine = (index, key, value) =>
    setMdLines(prev =>
      prev.map((l, i) => (i === index ? { ...l, [key]: value } : l))
    );

  const addMdLine = () => setMdLines(prev => [...prev, emptyLine()]);

  const removeMdLine = index =>
    setMdLines(prev =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );

  // Prefill every step from an existing deal / company template. The pay details
  // come back embedded under deal_users / *_scale / default_* as deal-specific
  // copies, so seed the option lists with them too. Used both when editing (the
  // `deal` prop) and when a manager picks an initial company template on step 1.
  const prefillFromDeal = source => {
    setSelectedId(
      source.template_id != null ? String(source.template_id) : null
    );
    setDealType(source.deal_type ?? "REP");
    setAllowOverride(!!source.has_override);

    setPopulateScaleId(
      source.override_scale?.id != null
        ? String(source.override_scale.id)
        : null
    );
    setOverrideDocumentTextId(
      source.override_scale?.document_text_id ??
        source.override_document_text_id ??
        null
    );
    setOverrideLines(linesFromScaleObj(source.override_scale));

    setMdPopulateScaleId(
      source.md_scale?.id != null ? String(source.md_scale.id) : null
    );
    setMdDocumentTextId(
      source.md_scale?.document_text_id ?? source.md_document_text_id ?? null
    );
    setMdLines(linesFromScaleObj(source.md_scale));

    const dealUsers = Array.isArray(source.deal_users) ? source.deal_users : [];
    const split = !!source.is_split && source.deal_type === "MD";
    setSplitMd(split);

    // Seed option lists with the embedded (copied) pay details that aren't in
    // the year's default lists, so the selects can render the selections.
    const baseUpfront = Array.isArray(upfrontPay) ? upfrontPay : [];
    setCreatedUpfront(
      dealUsers
        .map(u => u.upfront_pay)
        .filter(Boolean)
        .filter(up => !baseUpfront.some(x => String(x.id) === String(up.id)))
        .map(up => ({
          ...up,
          name:
            up.name ??
            UPFRONT_PAY_TYPE_LABEL_BY_VALUE[up.upfront_pay_type] ??
            up.upfront_pay_type ??
            "Upfront Pay",
        }))
    );
    const baseBonuses = Array.isArray(defaultBonuses) ? defaultBonuses : [];
    setCreatedBonuses(
      dealUsers
        .flatMap(u => bonusesFromUser(u))
        .filter(b => !baseBonuses.some(x => String(x.id) === String(b.id)))
    );
    const dealExpenses = Array.isArray(source.default_expenses)
      ? source.default_expenses
      : Array.isArray(source.expenses)
        ? source.expenses
        : [];
    setAddedExpenses(dealExpenses);

    if (split) {
      setSplitUsers(
        dealUsers.map((u, i) => ({
          name: `User ${i + 1}`,
          percentage: u.deal_percentage ?? "",
        }))
      );
      const commissionByUser = {};
      const upfrontByUser = {};
      const bonusesByUser = {};
      dealUsers.forEach((u, i) => {
        commissionByUser[i] = {
          populateScaleId:
            u.commission_scale?.id != null
              ? String(u.commission_scale.id)
              : null,
          lines: commissionLinesFromUser(u),
        };
        upfrontByUser[i] =
          u.upfront_pay?.id != null ? String(u.upfront_pay.id) : null;
        bonusesByUser[i] = bonusesFromUser(u).map(b => String(b.id));
      });
      setUserCommission(commissionByUser);
      setUserUpfront(upfrontByUser);
      setUserBonuses(bonusesByUser);

      setCommissionPopulateScaleId(null);
      setCommissionLines([emptyLine()]);
      resetNewUpfrontForm();
      setAddedBonuses([]);
    } else {
      const primary = dealUsers[0] || {};
      setCommissionPopulateScaleId(
        primary.commission_scale?.id != null
          ? String(primary.commission_scale.id)
          : null
      );
      setCommissionLines(commissionLinesFromUser(primary));
      prefillUpfrontForm(primary.upfront_pay);
      setAddedBonuses(bonusesFromUser(primary));

      setSplitUsers([]);
      setUserCommission({});
      setUserUpfront({});
      setUserBonuses({});
    }

    setDealName(source.name ?? "");
    // Manager deals are always customizable.
    setIsCustomizable(isManagerDeal ? true : (source.is_customizable ?? true));
  };

  // Empty defaults for a brand-new deal (nothing to prefill from yet).
  const resetDealState = () => {
    setSelectedId(null);
    setDealType("REP");
    setAllowOverride(false);
    setPopulateScaleId(null);
    setOverrideLines([emptyLine()]);
    setOverrideDocumentTextId(null);
    setMdPopulateScaleId(null);
    setMdLines([emptyLine()]);
    setMdDocumentTextId(null);
    setCommissionPopulateScaleId(null);
    setCommissionLines([emptyLine()]);
    resetNewUpfrontForm();
    setAddedBonuses([]);
    setAddedExpenses([]);
    setDealName("");
    setIsCustomizable(true);
  };

  useEffect(() => {
    if (!opened) return;

    // Reset everything that isn't prefilled from an existing deal. Embedded mode
    // skips the agreement/template step and starts at the deal-type step.
    setName("");
    setFile(null);
    setSavedAgreement(null);
    setRoles([]);
    setEditingRole(null);
    setRoleModalOpened(false);
    setSplitMd(false);
    setSplitUsers([]);
    setUserCommission({});
    setUserUpfront({});
    setUserBonuses({});
    setShowNewUpfront(false);
    setUpPayType(null);
    setUpFixedAmount("");
    setUpFixedPercentage("");
    setUpScaleLines([emptyScaleLine()]);
    setCreatedUpfront([]);
    setCreatedBonuses([]);
    setShowAddBonus(false);
    setBonusFormKey(prev => prev + 1);
    setExpenseFormKey(prev => prev + 1);
    setManagerTemplateId(null);

    if (deal) {
      // Edit mode: prefill each step from the existing deal template.
      setStep(hideTemplateStep ? 2 : 1);
      prefillFromDeal(deal);
    } else if (managerCreate) {
      // Manager add-flow: step 1 picks an initial company template, then the
      // remaining steps are prefilled from it on Continue.
      setStep(1);
      resetDealState();
    } else {
      setStep(hideTemplateStep ? 2 : 1);
      resetDealState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, deal]);

  // managerCreate mode: only customizable company templates can be a base, and
  // selecting one prefills the wizard before advancing to the deal-type step.
  const customizableTemplates = (
    Array.isArray(companyTemplates) ? companyTemplates : []
  ).filter(t => t.is_customizable);

  const handleSelectInitialDeal = () => {
    const template = customizableTemplates.find(
      t => String(t.id) === String(managerTemplateId)
    );
    if (!template) return;
    prefillFromDeal(template);
    setStep(2);
  };

  const isNew = selectedId === NEW_AGREEMENT_ID;
  const selected = agreements.find(a => String(a.id) === String(selectedId));

  // Persist recipient changes to the just-saved template.
  const saveRoles = async updatedRoles => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt || !savedAgreement?.id) return;
      await esignatureService.updateTemplate(
        savedAgreement.id,
        { roles_metadata: updatedRoles },
        jwt
      );
    } catch (err) {
      handleGenericError(err, "Failed to save recipients");
    }
  };

  const handleAddRole = () => {
    setEditingRole(null);
    setRoleModalOpened(true);
  };

  const handleEditRole = role => {
    setEditingRole(role);
    setRoleModalOpened(true);
  };

  const handleDeleteRole = role => {
    const updated = roles
      .filter(r => r.id !== role.id)
      .map((r, idx) => ({ ...r, signing_order: idx + 1 }));
    setRoles(updated);
    saveRoles(updated);
  };

  const handleRoleSubmit = ({ name: roleName, type, color }) => {
    let updated;
    if (editingRole) {
      updated = roles.map(r =>
        r.id === editingRole.id
          ? { ...r, full_name: roleName, role: type, color }
          : r
      );
    } else {
      updated = [
        ...roles,
        {
          id: `role-${roles.length + 1}-${roleName}`,
          role: type,
          full_name: roleName,
          email: `${roleName.toLowerCase().replace(/\s+/g, "")}@example.com`,
          color,
          signing_order: roles.length + 1,
        },
      ];
    }
    setRoles(updated);
    setRoleModalOpened(false);
    setEditingRole(null);
    saveRoles(updated);
  };

  const handleSaveAgreement = async () => {
    if (!name.trim()) {
      showError("Please enter a name");
      return;
    }
    if (!file) {
      showError("Please select a file");
      return;
    }

    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name.trim());
      if (sellingYearId != null) {
        formData.append("selling_year_id", sellingYearId);
      }

      const result = await esignatureService.createTemplate(formData, jwt);
      const created = result?.data || result || {};
      setSavedAgreement(created);
      setRoles(
        Array.isArray(created.roles_metadata) ? created.roles_metadata : []
      );
      showSuccess("Agreement saved successfully!");
    } catch (err) {
      handleGenericError(err, "Failed to save agreement");
    } finally {
      setSaving(false);
    }
  };

  const isSplit = dealType === "MD" && splitMd;

  // When splitting, Continue is blocked until every user has an upfront
  // selected. The non-split flow edits the upfront inline and is optional, so it
  // never blocks Continue.
  const upfrontStepInvalid = isSplit
    ? splitUsers.length === 0 || splitUsers.some((_, i) => !getUserUpfront(i))
    : false;

  // Resolve a full upfront pay record / bonus records from selected ids so each
  // user carries the complete details, not just references.
  const resolveUpfront = id =>
    id ? allUpfront.find(u => String(u.id) === String(id)) || null : null;
  const resolveBonuses = ids =>
    (Array.isArray(ids) ? ids : [])
      .map(id => allBonuses.find(b => String(b.id) === String(id)))
      .filter(Boolean);

  // Map a resolved bonus record to the deal DTO's bonus shape.
  const buildBonusPayload = bonus => ({
    name: bonus.name,
    bonus_type_category: bonus.bonus_type_category,
    bonus_type_earned_by_item: bonus.bonus_type_earned_by_item,
    bonus_type_earned_by_type: bonus.bonus_type_earned_by_type,
    item_name: bonus.item_name ?? null,
    item_monetary_value: bonus.item_monetary_value ?? null,
    bonus_amount: bonus.bonus_amount ?? null,
    bonus_revenue_amount: bonus.bonus_revenue_amount ?? null,
    bonus_accounts_amount: bonus.bonus_accounts_amount ?? null,
    bonus_recruits_amount: bonus.bonus_recruits_amount ?? null,
    recurring_initial_amount: bonus.recurring_initial_amount ?? null,
    recurring_repeated_amount: bonus.recurring_repeated_amount ?? null,
    recurring_start_date: bonus.recurring_start_date ?? null,
    recurring_end_date: bonus.recurring_end_date ?? null,
    recurring_schedule_type: bonus.recurring_schedule_type ?? null,
    recurring_day_of_week: bonus.recurring_day_of_week ?? null,
    recurring_day_of_month: bonus.recurring_day_of_month ?? null,
  });

  // Map a resolved expense record to the deal DTO's expense shape.
  const buildExpensePayload = expense => ({
    name: expense.name,
    expense_type_category: expense.expense_type_category,
    backend_percentage: expense.backend_percentage ?? null,
    backend_fixed_amount: expense.backend_fixed_amount ?? null,
    recurring_frontend_amount: expense.recurring_frontend_amount ?? null,
    recurring_schedule_type: expense.recurring_schedule_type ?? null,
    recurring_day_of_week: expense.recurring_day_of_week ?? null,
    recurring_day_of_month: expense.recurring_day_of_month ?? null,
  });

  // The upfront pay snapshot (split flow: from a resolved/selected record).
  const buildUpfrontPay = upfront => {
    if (!upfront) return undefined;
    const rest = { ...upfront };
    delete rest.document_text_id;
    delete rest.name;
    return rest;
  };

  // The upfront pay snapshot built straight from the inline form fields
  // (non-split flow). Returns undefined when nothing has been entered.
  const buildUpfrontFromForm = () => {
    if (!upPayType) return undefined;
    const upfront = {
      upfront_pay_type: upPayType,
    };
    if (upPayType === "FIXED_AMOUNT_PER_ACCOUNT") {
      upfront.fixed_amount =
        upFixedAmount === "" ? null : Number(upFixedAmount);
    } else if (upPayType === "FIXED_PERCENT_ACV") {
      upfront.fixed_percentage =
        upFixedPercentage === "" ? null : Number(upFixedPercentage);
    } else if (upShowScale) {
      const valueKey = upIsAmountScale ? "amount" : "percentage";
      upfront.scale_lines = upScaleLines
        .filter(line => line.accounts !== "" && line.value !== "")
        .map(line => ({
          accounts_amount: Number(line.accounts),
          [valueKey]: Number(line.value),
        }));
    }
    return upfront;
  };

  // The deal users (splits). Each carries a commission scale (+ tier lines), an
  // optional upfront pay snapshot, and its bonuses — matching the deal DTO.
  const buildUsers = () => {
    if (isSplit) {
      return splitUsers.map((u, index) => {
        const editor = getUserCommission(index);
        return {
          name: u.name,
          percentage: Number(u.percentage) || 0,
          commission_scale_lines: toScaleLinesPayload(editor.lines),
          upfront_pay: buildUpfrontPay(resolveUpfront(getUserUpfront(index))),
          bonuses: resolveBonuses(getUserBonuses(index)).map(buildBonusPayload),
        };
      });
    }
    return [
      {
        commission_scale_lines: toScaleLinesPayload(commissionLines),
        upfront_pay: buildUpfrontFromForm(),
        bonuses: addedBonuses.map(buildBonusPayload),
      },
    ];
  };

  // The deal-shaped payload (no template/name/id) shared by the save, review,
  // and embedded "edit then continue" (onComplete) flows. Matches the deal DTO.
  const buildDealPayload = () => {
    const payload = {
      selling_year_id: sellingYearId,
      deal_type: dealType,
      users: buildUsers(),
      expenses: addedExpenses.map(buildExpensePayload),
    };
    if (dealType === "REP") {
      payload.allow_override = allowOverride;
      if (allowOverride && overrideDocumentTextId != null) {
        payload.override_document_text_id = overrideDocumentTextId;
      }
      payload.override_scale_lines = allowOverride
        ? toScaleLinesPayload(overrideLines)
        : undefined;
    } else if (dealType === "MD") {
      if (mdDocumentTextId != null) {
        payload.md_document_text_id = mdDocumentTextId;
      }
      payload.md_scale_lines = toScaleLinesPayload(mdLines);
    }
    return payload;
  };

  // Save a brand new upfront pay type, returning its id.
  const saveNewUpfront = async () => {
    if (!upPayType) {
      showError("Please select a pay type");
      return null;
    }
    if (upPayType === "FIXED_AMOUNT_PER_ACCOUNT" && upFixedAmount === "") {
      showError("Please enter a fixed amount");
      return null;
    }
    if (upPayType === "FIXED_PERCENT_ACV" && upFixedPercentage === "") {
      showError("Please enter a fixed percentage");
      return null;
    }
    if (
      upShowScale &&
      upScaleLines.some(l => l.accounts === "" || l.value === "")
    ) {
      showError("Please complete all scale lines");
      return null;
    }

    const payload = {
      name: upfrontName,
      upfront_pay_type: upPayType,
      is_default: false,
      selling_year_id: sellingYearId,
    };
    if (upPayType === "FIXED_AMOUNT_PER_ACCOUNT") {
      payload.fixed_amount = Number(upFixedAmount);
    } else if (upPayType === "FIXED_PERCENT_ACV") {
      payload.fixed_percentage = Number(upFixedPercentage);
    } else if (upShowScale) {
      const valueKey = upIsAmountScale ? "amount" : "percentage";
      payload.scale_lines = upScaleLines.map(line => ({
        accounts: Number(line.accounts),
        [valueKey]: Number(line.value),
      }));
    }

    try {
      setSavingUpfront(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/ps/upfront-pay"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        const created = result?.data || result || {};
        // Make sure the saved type has everything needed to render read-only.
        const createdItem = { ...payload, ...created };
        setCreatedUpfront(prev => [...prev, createdItem]);
        showSuccess("Upfront pay type created successfully!");
        return createdItem;
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to create upfront pay type"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to create upfront pay type");
    } finally {
      setSavingUpfront(false);
    }
    return null;
  };

  // Save a new upfront type and, when splitting, collapse the shared create
  // form so the new type can then be picked per user.
  const handleSaveNewUpfront = async () => {
    const created = await saveNewUpfront();
    if (created) {
      setShowNewUpfront(false);
      resetNewUpfrontForm();
    }
  };

  // The full "create a new upfront pay type" form, shared by the non-split
  // (inline) and split (one shared creator) flows.
  const renderNewUpfrontForm = ({ withSaveButton = true } = {}) => (
    <Stack gap="md">
      <Select
        label="Pay Type"
        placeholder="Select pay type"
        data={upfrontPayTypeOptions}
        value={upPayType}
        onChange={setUpPayType}
        searchable
        required
      />

      {upPayType === "FIXED_AMOUNT_PER_ACCOUNT" && (
        <NumberInput
          label="Fixed Amount"
          placeholder="Enter fixed amount"
          value={upFixedAmount}
          onChange={setUpFixedAmount}
          min={0}
          prefix="$"
          thousandSeparator=","
          decimalScale={2}
          fixedDecimalScale
        />
      )}

      {upPayType === "FIXED_PERCENT_ACV" && (
        <NumberInput
          label="Fixed Percentage"
          placeholder="Enter fixed percentage"
          value={upFixedPercentage}
          onChange={setUpFixedPercentage}
          min={0}
          suffix="%"
        />
      )}

      {upShowScale && (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Scale
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              Account Number
            </Text>
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              {upIsAmountScale ? "Amount" : "Percentage of ACV"}
            </Text>
            <div style={{ width: 28 }} />
          </Group>
          {upScaleLines.map((line, index) => (
            <Group key={index} gap="xs" wrap="nowrap" align="center">
              <NumberInput
                placeholder="Account number"
                value={line.accounts}
                onChange={value => updateUpScaleLine(index, "accounts", value)}
                min={0}
                style={{ flex: 1 }}
              />
              <NumberInput
                placeholder={upIsAmountScale ? "Amount" : "Percentage"}
                value={line.value}
                onChange={value => updateUpScaleLine(index, "value", value)}
                min={0}
                style={{ flex: 1 }}
                {...(upIsAmountScale
                  ? {
                      prefix: "$",
                      thousandSeparator: ",",
                      decimalScale: 2,
                      fixedDecimalScale: true,
                    }
                  : { suffix: "%" })}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => removeUpScaleLine(index)}
                disabled={upScaleLines.length === 1}
                aria-label="Remove scale line"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Group>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={addUpScaleLine}
            >
              Add Scale Line
            </Button>
          </Group>
        </Stack>
      )}

      {withSaveButton && (
        <Group justify="flex-end">
          <Button onClick={handleSaveNewUpfront} loading={savingUpfront}>
            Save Upfront Pay Type
          </Button>
        </Group>
      )}
    </Stack>
  );

  // Save the configured custom deal directly via the API. Mirrors the payload
  // the add-default-deal modal on the selling years page sends (scale ids, not
  // lines), which is what /v1/ps/default-deal accepts.
  const handleSave = async () => {
    const agreement = isNew ? savedAgreement : selected;
    if (!dealName.trim()) {
      showError("Please enter a deal template name");
      return;
    }
    const payload = {
      name: dealName.trim(),
      template_id: agreement?.id ?? deal?.template_id ?? null,
      is_customizable: isCustomizable,
      ...buildDealPayload(),
    };

    try {
      setSavingDeal(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const endpoint = isManagerDeal
        ? isEditing
          ? `/v1/ps/manager-deal/${editingId}`
          : "/v1/ps/manager-deal"
        : isEditing
          ? `/v1/ps/default-deal/${editingId}`
          : "/v1/ps/default-deal";

      const response = await fetch(getApiEndpoint(endpoint), {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showSuccess(
          isEditing
            ? "Deal template updated successfully!"
            : "Custom deal created successfully!"
        );
        if (onSaved) onSaved();
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message ||
            `Failed to ${isEditing ? "update" : "create"} deal template`
        );
      }
    } catch (err) {
      handleGenericError(
        err,
        `Failed to ${isEditing ? "update" : "create"} deal template`
      );
    } finally {
      setSavingDeal(false);
    }
  };

  // Embedded mode: hand the edited deal payload back to the caller instead of
  // persisting it here.
  const handleComplete = () => {
    if (onComplete) onComplete(buildDealPayload());
  };

  const step1Disabled = !selectedId || (isNew && !savedAgreement);

  // When splitting an MD deal, the user percentages must total exactly 100.
  const splitTotal = splitUsers.reduce(
    (sum, u) => sum + (Number(u.percentage) || 0),
    0
  );
  const finishDisabled = dealType === "MD" && splitMd && splitTotal !== 100;

  const content = (
    <>
      {step === 1 && managerCreate ? (
        <Stack gap="md">
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Initial Deal
            </Text>
            <Text size="xs" c="dimmed">
              Select a company deal template to start from.
            </Text>
          </Stack>

          {customizableTemplates.length === 0 ? (
            <Text size="sm" c="dimmed">
              No customizable company deal templates available.
            </Text>
          ) : (
            <Stack gap="sm">
              {customizableTemplates.map(template => {
                const isSelected =
                  String(template.id) === String(managerTemplateId);
                return (
                  <Card
                    key={template.id}
                    withBorder
                    radius="md"
                    padding="sm"
                    onClick={() => setManagerTemplateId(template.id)}
                    style={{
                      cursor: "pointer",
                      borderColor: isSelected
                        ? "var(--mantine-color-blue-5)"
                        : undefined,
                      borderWidth: isSelected ? 2 : 1,
                    }}
                  >
                    <Group gap="xs">
                      <Text fw={600} size="sm">
                        {template.name}
                      </Text>
                      <Badge
                        size="sm"
                        variant="light"
                        color={template.deal_type === "MD" ? "grape" : "green"}
                      >
                        {template.deal_type}
                      </Badge>
                    </Group>
                    {template.description && (
                      <Text size="xs" c="dimmed" mt={4}>
                        {template.description}
                      </Text>
                    )}
                  </Card>
                );
              })}
            </Stack>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!managerTemplateId}
              onClick={handleSelectInitialDeal}
            >
              Continue
            </Button>
          </Group>
        </Stack>
      ) : step === 1 ? (
        <Stack gap="md">
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Agreement
            </Text>
            <Text size="xs" c="dimmed">
              Select an uploaded agreement to base the deal on, or create a new
              one.
            </Text>
          </Stack>

          <Stack gap="sm">
            {agreements.map(agreement => {
              const isSelected = String(agreement.id) === String(selectedId);
              return (
                <Card
                  key={agreement.id}
                  withBorder
                  radius="md"
                  padding="sm"
                  onClick={() => setSelectedId(agreement.id)}
                  style={{
                    cursor: "pointer",
                    borderColor: isSelected
                      ? "var(--mantine-color-blue-5)"
                      : undefined,
                    borderWidth: isSelected ? 2 : 1,
                  }}
                >
                  <Group gap="xs" wrap="nowrap">
                    <IconFileText size={18} />
                    <Text fw={600} size="sm">
                      {agreement.name ?? "Untitled Agreement"}
                    </Text>
                  </Group>
                </Card>
              );
            })}

            {/* New agreement option */}
            <Card
              withBorder
              radius="md"
              padding="sm"
              onClick={() => setSelectedId(NEW_AGREEMENT_ID)}
              style={{
                cursor: "pointer",
                borderColor: isNew ? "var(--mantine-color-blue-5)" : undefined,
                borderWidth: isNew ? 2 : 1,
                borderStyle: "dashed",
              }}
            >
              <Group gap="xs" wrap="nowrap">
                <ThemeIcon color="blue" variant="light" size={24} radius="sm">
                  <IconPlus size={16} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  New Agreement
                </Text>
              </Group>
            </Card>
          </Stack>

          {isNew && (
            <Stack gap="md">
              <TextInput
                label="Name"
                placeholder="e.g., Rep Agreement"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={!!savedAgreement}
                required
              />
              <FileInput
                label="Document"
                placeholder="Upload a PDF"
                accept="application/pdf"
                value={file}
                onChange={setFile}
                disabled={!!savedAgreement}
                clearable
                required
              />

              {!savedAgreement && (
                <Group justify="flex-end">
                  <Button
                    onClick={handleSaveAgreement}
                    loading={saving}
                    disabled={!name.trim() || !file}
                  >
                    Save
                  </Button>
                </Group>
              )}

              {savedAgreement && (
                <RecipientsStep
                  roles={roles}
                  onAdd={handleAddRole}
                  onEdit={handleEditRole}
                  onDelete={handleDeleteRole}
                />
              )}
            </Stack>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button disabled={step1Disabled} onClick={() => setStep(2)}>
              Continue
            </Button>
          </Group>
        </Stack>
      ) : step === 2 ? (
        <Stack gap="md">
          <Radio.Group
            label="Deal Type"
            value={dealType}
            onChange={setDealType}
          >
            <Group gap="lg" mt="xs">
              <Radio value="REP" label="Rep" />
              <Radio value="MD" label="MD" />
            </Group>
          </Radio.Group>

          {dealType === "REP" && (
            <Switch
              label="Allow Override"
              checked={allowOverride}
              onChange={e => setAllowOverride(e.currentTarget.checked)}
            />
          )}

          {dealType === "MD" && (
            <Stack gap="sm">
              <Select
                label="Populate from scale"
                placeholder="Select an MD scale"
                data={mdScaleOptions}
                value={mdPopulateScaleId}
                onChange={handlePopulateMdScale}
                searchable
                clearable
              />

              <EditableScaleCard
                title="MD SCALE"
                percentLabel="MD %"
                lines={mdLines}
                onChangeLine={updateMdLine}
                onAddLine={addMdLine}
                onRemoveLine={removeMdLine}
              />
            </Stack>
          )}

          {dealType === "MD" && (
            <Switch
              label="Split MD"
              checked={splitMd}
              onChange={e => setSplitMd(e.currentTarget.checked)}
            />
          )}

          {dealType === "MD" && splitMd && (
            <MdSplitSection
              rows={splitUsers}
              onAddUser={handleAddSplitUser}
              onChangePercent={updateSplitUser}
              onRemoveUser={removeSplitUser}
            />
          )}

          {dealType === "REP" && allowOverride && (
            <Stack gap="sm">
              <Select
                label="Populate from scale"
                placeholder="Select an override scale"
                data={overrideScaleOptions}
                value={populateScaleId}
                onChange={handlePopulateFromScale}
                searchable
                clearable
              />

              <EditableScaleCard
                title="OVERRIDE SCALE"
                percentLabel="OVERRIDE %"
                lines={overrideLines}
                onChangeLine={updateOverrideLine}
                onAddLine={addOverrideLine}
                onRemoveLine={removeOverrideLine}
              />
            </Stack>
          )}

          <Group justify="space-between" mt="md">
            {hideTemplateStep ? (
              onBack ? (
                <Button variant="subtle" onClick={onBack}>
                  Back
                </Button>
              ) : (
                <span />
              )
            ) : (
              <Button variant="subtle" onClick={() => setStep(1)}>
                Back
              </Button>
            )}
            <Button onClick={() => setStep(3)} disabled={finishDisabled}>
              Continue
            </Button>
          </Group>
        </Stack>
      ) : step === 3 ? (
        <Stack gap="md">
          <Text size="sm" fw={600}>
            Commission Scale
          </Text>

          {isSplit ? (
            splitUsers.map((u, index) => {
              const editor = getUserCommission(index);
              return (
                <Stack key={index} gap="sm">
                  <Text size="sm" fw={500}>
                    {u.name}
                  </Text>
                  <Select
                    label="Populate from scale"
                    placeholder="Select a commission scale"
                    data={commissionScaleOptions}
                    value={editor.populateScaleId}
                    onChange={value =>
                      handlePopulateUserCommission(index, value)
                    }
                    searchable
                    clearable
                  />
                  <EditableScaleCard
                    title="COMMISSION SCALE"
                    percentLabel="COMMISSION %"
                    lines={editor.lines}
                    onChangeLine={(lineIndex, key, value) =>
                      updateUserCommissionLine(index, lineIndex, key, value)
                    }
                    onAddLine={() => addUserCommissionLine(index)}
                    onRemoveLine={lineIndex =>
                      removeUserCommissionLine(index, lineIndex)
                    }
                  />
                </Stack>
              );
            })
          ) : (
            <Stack gap="sm">
              <Select
                label="Populate from scale"
                placeholder="Select a commission scale"
                data={commissionScaleOptions}
                value={commissionPopulateScaleId}
                onChange={handlePopulateCommission}
                searchable
                clearable
              />
              <EditableScaleCard
                title="COMMISSION SCALE"
                percentLabel="COMMISSION %"
                lines={commissionLines}
                onChangeLine={updateCommissionLine}
                onAddLine={addCommissionLine}
                onRemoveLine={removeCommissionLine}
              />
            </Stack>
          )}

          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={() => setStep(2)}>
              Back
            </Button>
            <Button onClick={() => setStep(4)}>Continue</Button>
          </Group>
        </Stack>
      ) : step === 4 ? (
        <Stack gap="md">
          <Text size="sm" fw={600}>
            Upfront Pay
          </Text>
          {isSplit ? (
            <>
              {splitUsers.map((u, index) => {
                const resolved = allUpfront.find(
                  up => String(up.id) === String(getUserUpfront(index))
                );
                return (
                  <Stack key={index} gap="xs">
                    <Text size="sm" fw={500}>
                      {u.name}
                    </Text>
                    <Select
                      label="Upfront Pay"
                      placeholder="Select an upfront pay option"
                      data={existingUpfrontOptions}
                      value={getUserUpfront(index)}
                      onChange={value => setUserUpfrontFor(index, value)}
                      searchable
                    />
                    {resolved && <UpfrontPayDisplay item={resolved} />}
                  </Stack>
                );
              })}

              {showNewUpfront ? (
                renderNewUpfrontForm()
              ) : (
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setShowNewUpfront(true)}
                  style={{ alignSelf: "flex-start" }}
                >
                  Add Upfront Pay Type
                </Button>
              )}
            </>
          ) : (
            renderNewUpfrontForm({ withSaveButton: false })
          )}

          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={() => setStep(3)}>
              Back
            </Button>
            <Button onClick={() => setStep(5)} disabled={upfrontStepInvalid}>
              Continue
            </Button>
          </Group>
        </Stack>
      ) : step === 5 ? (
        <Stack gap="md">
          {isSplit ? (
            <>
              {splitUsers.map((u, index) => (
                <Stack key={index} gap="xs">
                  <Text size="sm" fw={500}>
                    {u.name}
                  </Text>
                  <MultiSelect
                    label="Bonuses"
                    placeholder={
                      bonusOptions.length
                        ? "Select bonuses"
                        : "No bonuses available"
                    }
                    data={bonusOptions}
                    value={getUserBonuses(index)}
                    onChange={value => setUserBonusesFor(index, value)}
                    disabled={!bonusOptions.length}
                    searchable
                    clearable
                  />
                  {getUserBonuses(index)
                    .map(id =>
                      allBonuses.find(b => String(b.id) === String(id))
                    )
                    .filter(Boolean)
                    .map(bonus => (
                      <BonusDisplay key={bonus.id} item={bonus} />
                    ))}
                </Stack>
              ))}

              {showAddBonus ? (
                <AddBonusForm
                  sellingYearId={sellingYearId}
                  companyBonusTypes={companyBonusTypes}
                  onCreated={handleBonusCreated}
                  onCancel={() => setShowAddBonus(false)}
                />
              ) : (
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setShowAddBonus(true)}
                  style={{ alignSelf: "flex-start" }}
                >
                  Add Bonus
                </Button>
              )}
            </>
          ) : (
            <>
              {addedBonuses.map((bonus, index) => (
                <Card key={index} padding="sm" radius="md" withBorder>
                  <Group
                    justify="space-between"
                    wrap="nowrap"
                    align="flex-start"
                  >
                    <BonusDisplay item={bonus} />
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => removeAddedBonus(index)}
                      aria-label="Remove bonus"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Card>
              ))}

              <AddBonusForm
                key={bonusFormKey}
                inline
                sellingYearId={sellingYearId}
                companyBonusTypes={companyBonusTypes}
                onCreated={handleInlineBonusAdded}
              />
            </>
          )}

          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={() => setStep(4)}>
              Back
            </Button>
            <Button onClick={() => setStep(6)}>Continue</Button>
          </Group>
        </Stack>
      ) : step === 6 ? (
        <Stack gap="md">
          {addedExpenses.map((expense, index) => (
            <Card key={index} padding="sm" radius="md" withBorder>
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <ExpenseDisplay item={expense} />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => removeAddedExpense(index)}
                  aria-label="Remove expense"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Card>
          ))}

          <AddExpenseForm
            key={expenseFormKey}
            inline
            sellingYearId={sellingYearId}
            companyExpenseTypes={companyExpenseTypes}
            onCreated={handleInlineExpenseAdded}
          />

          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={() => setStep(5)}>
              Back
            </Button>
            <Button
              onClick={() => {
                // Embedded (add-deal) flow: skip the review step — the review is
                // shown on the modal's final direct-seller page instead.
                if (onComplete) {
                  handleComplete();
                  return;
                }
                if (!dealName.trim()) {
                  setDealName(isNew ? name : (selected?.name ?? ""));
                }
                setStep(7);
              }}
            >
              Continue
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="md">
          {!onComplete && (
            <TextInput
              label="Deal Template Name"
              placeholder="e.g., Standard Rep Deal"
              value={dealName}
              onChange={e => setDealName(e.target.value)}
              required
              data-autofocus
            />
          )}

          {!onComplete && !isManagerDeal && (
            <Switch
              label="Customizable"
              checked={isCustomizable}
              onChange={e => setIsCustomizable(e.currentTarget.checked)}
            />
          )}

          <DealReview
            {...buildDealPayload()}
            name={dealName || (isNew ? name : selected?.name) || "Deal"}
            is_customizable={isCustomizable}
            template_id={
              (isNew ? savedAgreement?.id : selected?.template_id) ??
              deal?.template_id ??
              null
            }
          />

          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={() => setStep(6)}>
              Back
            </Button>
            {onComplete ? (
              <Button onClick={handleComplete}>Continue</Button>
            ) : (
              <Button onClick={handleSave} loading={savingDeal}>
                {isEditing ? "Update" : "Save"}
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </>
  );

  return (
    <>
      {embedded ? (
        content
      ) : (
        <Modal
          opened={opened}
          onClose={onClose}
          title={
            hideTemplateStep
              ? "Edit Deal"
              : isEditing
                ? "Edit Deal Template"
                : "Add Deal Template"
          }
          size="lg"
        >
          {content}
        </Modal>
      )}

      <RoleFormModal
        opened={roleModalOpened}
        onClose={() => {
          setRoleModalOpened(false);
          setEditingRole(null);
        }}
        onSubmit={handleRoleSubmit}
        existingRoles={roles}
        role={editingRole}
      />
    </>
  );
}

export default CreateCustomDealModal;
